import { Text, View, Document, Page, Link, Image } from '@react-pdf/renderer';
import { PageHeaderLogo, styles, PageHeader, Comment } from '../constants';
import { textStyles } from '../styles';
import {
  rootCauseMap,
  cpaStringLookup,
  riskLevels,
  cpaStatusTypes,
} from '../../audits';
import { customColors } from '../..';
import { getLocalTime } from '../../time';
import { useTranslation } from 'react-i18next';
import { Extension } from './extension';
import { imageExtensions } from '../../files';

export const CorrectiveActionDocument = ({ correctiveAction, company }) => {
  const {
    inspection,
    question,
    riskLevel,
    probability,
    impact,
    ACCEPT,
    FINAL,
    INTERIM,
    REVIEW,
    rootCause,
    rootCauseAnalysis,
    isObservation,
    creator,
    status,
    dateCreated,
    narrative,
    attachments,
  } = correctiveAction || {};

  const { t } = useTranslation();

  const actionItems = [INTERIM, FINAL, ACCEPT, REVIEW] || [];

  const color = riskLevels[riskLevel]?.color?.toUpperCase() || 'primary';

  return (
    !!correctiveAction && (
      <Document>
        <Page size="A4" style={styles.page}>
          <PageHeaderLogo company={company} workspace={null} />
          <PageHeader
            company={company}
            data={correctiveAction}
            title={correctiveAction.externalId}
            workspace={null}
            type="Corrective Action"
          />
          <Text
            style={textStyles.header}
          >{`Corrective Action ${correctiveAction.externalId}`}</Text>
          <View>
            <Text style={textStyles.leftText}>{`Status: ${
              t(cpaStatusTypes[status]?.title) || status
            }`}</Text>
            <Text
              style={[textStyles.question, { color: customColors.SECONDARY }]}
            >{`Created by ${creator.firstName} ${
              creator.lastName
            } ${getLocalTime(dateCreated).format('dddd MMMM Do YYYY')}`}</Text>
          </View>

          {!!inspection ? (
            <Text style={textStyles.gray}>
              {`${inspection?.template?.title} - ${correctiveAction?.type}`}
            </Text>
          ) : null}
          {!!question ? (
            <View>
              <Text style={[textStyles.question, { color: customColors.RED }]}>
                Failed Question
              </Text>
              <Text style={textStyles.questionLg}>{question.title}</Text>
            </View>
          ) : null}
          <Text style={textStyles.question}>{narrative}</Text>
          {!isObservation ? (
            <View>
              <Text style={styles.categoryHeader}>Risk Assessment</Text>
              <View style={textStyles.spacer} />
              <Text
                style={[textStyles.questionLg, { color: customColors[color] }]}
              >
                {`${riskLevel} ${!isObservation ? 'RISK' : ''}`}
              </Text>
              <Text style={textStyles.question}>{`Impact: ${impact}/5`}</Text>
              <Text
                style={textStyles.question}
              >{`Probability: ${probability}/5`}</Text>
            </View>
          ) : null}
          <Text style={styles.categoryHeader}>Root Cause</Text>
          <View style={textStyles.spacer} />
          <Text style={textStyles.question}>
            {rootCauseMap[rootCause] || 'No Root Cause Provided'}
          </Text>
          <Text style={textStyles.answer}>{rootCauseAnalysis || ''}</Text>
          {actionItems?.map((action) => {
            const {
              type,
              outcome,
              assignee,
              comments,
              participants,
              dueDate,
              dateCompleted,
              extension = [],
              attachments = [],
            } = action || {};
            return !!action ? (
              <View key={action.id}>
                <Text style={styles.categoryHeader}>
                  {type ? cpaStringLookup[type] : ''}
                </Text>
                <View>
                  <View style={textStyles.flexRowXS}>
                    <Text style={textStyles.question}>Due Date:</Text>
                    <Text style={textStyles.answer}>
                      {` ${
                        dueDate
                          ? getLocalTime(dueDate).format('dddd MMMM Do YYYY')
                          : 'None'
                      }`}
                    </Text>
                  </View>
                  <View>
                    <View style={textStyles.flexRowXS}>
                      <Text style={textStyles.question}>Date Completed:</Text>
                      <Text style={textStyles.answer}>
                        {`${
                          dateCompleted
                            ? getLocalTime(dateCompleted).format(
                                'dddd MMMM Do YYYY',
                              )
                            : 'None'
                        }`}
                      </Text>
                    </View>
                    <View style={textStyles.flexRowXS}>
                      <Text style={textStyles.question}>Assignee:</Text>
                      <Text
                        style={textStyles.answer}
                      >{`${assignee?.firstName} ${assignee?.lastName}`}</Text>
                    </View>
                    {!!participants?.length ? (
                      <View>
                        <View style={textStyles.flexRowXS}>
                          <Text style={textStyles.question}>Delegates: </Text>
                          {participants?.map((p) => (
                            <Text
                              style={textStyles.answer}
                              key={p.id}
                            >{`${p.firstName} ${p.lastName}`}</Text>
                          ))}
                        </View>
                      </View>
                    ) : null}
                    <View style={textStyles.spacer} />
                    <Text style={textStyles.answer}>{outcome}</Text>
                    {!!comments?.length ? (
                      <View>
                        <View style={textStyles.spacer} />
                        {comments?.map((comment) => (
                          <Comment key={comment.id} comment={comment} />
                        ))}
                      </View>
                    ) : null}
                  </View>
                </View>
                {!!extension?.length && (
                  <View>
                    <View style={textStyles.spacer} />
                    <Text style={textStyles.question}>Extensions</Text>
                    <Extension extensions={extension} />
                  </View>
                )}
                {!!attachments?.length ? (
                  <View>
                    <Text style={textStyles.categoryHeader}>
                      Action Attachments
                    </Text>
                    {attachments?.map((file, index) => {
                      const { imageUrl, url, extension, name, id } = file || {};
                      const src = imageUrl || url;
                      const ext = extension || src?.split('.')?.pop();
                      return imageExtensions.includes(ext) ? (
                        <Image
                          style={styles.templateImage}
                          src={src}
                          key={`${id}-${index}`}
                        />
                      ) : (
                        <View key={id}>
                          <Text
                            style={styles.text}
                          >{`File Type: ${extension}`}</Text>
                          <Link style={styles.linkLeft} src={src}>
                            {name}
                          </Link>
                        </View>
                      );
                    })}
                  </View>
                ) : null}
              </View>
            ) : null;
          })}
          <View style={textStyles.spacer} />
          {!!attachments?.length ? (
            <View>
              <Text style={textStyles.categoryHeader}>Attachments</Text>
              {attachments?.map((file, index) => {
                const { imageUrl, url, extension, name, id } = file || {};
                const src = imageUrl || url;
                const ext = extension || src?.split('.')?.pop();
                return imageExtensions.includes(ext) ? (
                  <Image
                    style={styles.templateImage}
                    src={src}
                    key={`${id}-${index}`}
                  />
                ) : (
                  <View key={id}>
                    <Text style={styles.text}>{`File Type: ${extension}`}</Text>
                    <Link style={styles.linkLeft} src={src}>
                      {name}
                    </Link>
                  </View>
                );
              })}
            </View>
          ) : null}
        </Page>
      </Document>
    )
  );
};
